import * as React from "react";
import { useState } from "react";
import { graphql, useFragment } from "react-relay";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { Markdown } from "src/components/ui/Markdown";

import { DefaultRendering_toolCalls$key } from "./__generated__/DefaultRendering_toolCalls.graphql";
import { ToolCallButton } from "./ToolCallButton";
import { ToolCallSheet } from "./ToolCallSheet";

interface DefaultRenderingProps {
  toolCalls: DefaultRendering_toolCalls$key;
  toolContent: string;
  sources: any[];
}

const fragment = graphql`
  fragment DefaultRendering_toolCalls on GraphQLMessageToolCall @relay(plural: true) {
    ...ToolCallButton_toolCall
    ...ToolCallSheet_toolCall
  }
`;

export const DefaultRendering = React.memo<DefaultRenderingProps>(function DefaultRendering({
  toolCalls: toolCallsKey,
  toolContent,
  sources,
}) {
  const toolCalls = useFragment(fragment, toolCallsKey);
  const hasToolCalls = toolCalls.length > 0;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeToolIndex, setActiveToolIndex] = useState(0);

  const handleSheetOpen = (index: number) => {
    setActiveToolIndex(index);
    setIsSheetOpen(true);
  };

  const moveToNextTool = () => {
    if (activeToolIndex < toolCalls.length - 1) {
      setActiveToolIndex(activeToolIndex + 1);
    }
  };

  const moveToPrevTool = () => {
    if (activeToolIndex > 0) {
      setActiveToolIndex(activeToolIndex - 1);
    }
  };

  const activeTool = toolCalls[activeToolIndex];

  return (
    <>
      <div className="flex flex-col gap-2 border border-border p-2 rounded-md mt-1">
        <div className="flex flex-col gap-2">
          {hasToolCalls &&
            toolCalls.map((subToolCall, subIndex) => (
              <ToolCallButton
                key={subIndex}
                toolCall={subToolCall}
                onClick={() => handleSheetOpen(subIndex)}
              />
            ))}
          {!hasToolCalls && (
            <LoadingSpinner className="h-3.5 w-3.5 flex-shrink-0" />
          )}
        </div>
        {toolContent ? (
          <Markdown sources={sources}>{toolContent || ""}</Markdown>
        ) : null}
      </div>

      {isSheetOpen && activeTool && (
        <ToolCallSheet
          toolCall={activeTool}
          index={activeToolIndex}
          totalCount={toolCalls.length}
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          onNext={moveToNextTool}
          onPrevious={moveToPrevTool}
        />
      )}
    </>
  );
});